import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X, Sparkles, Loader2 } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { api, track } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PAYWALL } from "@/constants/testIds";
import { PromoRedeem } from "@/components/PromoRedeem";

const BENEFITS = [
  "Build confidence that doesn't depend on the next win",
  "Strengthen your relationships and friendships",
  "Create healthier boundaries — without guilt",
  "Find clarity when life feels overwhelming",
  "A private place to reflect, entirely your own",
  "Warm, substantive guidance whenever you need it",
];

export default function Paywall() {
  const { user, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const entitled = !!(user && (user.is_premium || user.role === "owner"));
  const [plan, setPlan] = useState("annual");
  const [config, setConfig] = useState(null);
  const [loadingCfg, setLoadingCfg] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [startingTrial, setStartingTrial] = useState(false);
  const trialUsed = !!(user?.access?.trial_used);
  const trialActive = !!(user?.access?.trial_active);
  const trialEnded = trialUsed && !trialActive && !entitled;
  const canStartTrial = !entitled && !trialUsed;

  const startTrial = async () => {
    setStartingTrial(true);
    try {
      const { data } = await api.post("/billing/start-trial");
      track("free_trial_started");
      setUser((u) => ({ ...(u || {}), is_trial_active: true, entitled: true, access: data.access }));
      await checkAuth();
      toast.success("Your 7-day free trial is active. Welcome to Sisterhood+.");
      navigate("/home");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start your trial. Please try again.");
    } finally {
      setStartingTrial(false);
    }
  };

  useEffect(() => {
    track("paywall_viewed");
    api.get("/billing/config")
      .then(({ data }) => setConfig(data))
      .catch(() => toast.error("Payments are temporarily unavailable. Please try again shortly."))
      .finally(() => setLoadingCfg(false));
  }, []);

  const applyDiscount = (d) => { setDiscount(d); if (d?.cadence) setPlan(d.cadence); };

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get("code");
    if (!code) return;
    api.post("/promo/redeem", { code }).then(({ data }) => {
      if (data.kind === "discount") { applyDiscount(data); toast.success(data.message || "Discount applied."); }
    }).catch(() => {});
  }, []);

  const selectPlan = (p) => { setPlan(p); if (discount && discount.cadence !== p) setDiscount(null); };
  const discountActive = !!(discount && discount.cadence === plan);
  const planId = config
    ? (discountActive ? discount.plan_id : (plan === "annual" ? config.annual_plan_id : config.monthly_plan_id))
    : null;

  const onApprove = async (data) => {
    setProcessing(true);
    try {
      await api.post("/billing/activate", { subscription_id: data.subscriptionID });
      track(plan === "annual" ? "subscription_started_annual" : "subscription_started", { plan });
      setUser((u) => ({ ...(u || {}), is_premium: true }));
      toast.success("Welcome to Sisterhood+. Your membership is active.");
      navigate("/home");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "We couldn't confirm your subscription. Please contact support.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFAF6] px-6 py-10 lg:py-14">
    <div className="max-w-4xl mx-auto">
      <div className="flex justify-end">
        {entitled && (
          <button data-testid={PAYWALL.closeButton} onClick={() => navigate("/home")}
            aria-label="Close" className="p-2 rounded-full hover:bg-[#F7F2EA]">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs uppercase tracking-[0.25em] text-[#C89B55] font-semibold">Sisterhood+ Membership</p>
        <h1 className="font-display text-4xl lg:text-5xl font-semibold mt-3 leading-tight">
          Invest in the woman you're becoming.
        </h1>
        <p className="text-[#59535B] mt-4 text-lg">
          Everything you need to grow — in one private, beautifully made space.
        </p>
      </div>

      {trialEnded && (
        <div className="mt-8 max-w-md mx-auto rounded-2xl border border-[#C89B55] bg-[#FBF4E7] p-6 text-center" data-testid="paywall-trial-ended">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Your trial has ended</p>
          <h2 className="font-display text-2xl font-semibold mt-1 text-[#291B2D]">We loved having you these 7 days.</h2>
          <p className="text-sm text-[#59535B] mt-2">Subscribe to pick up right where you left off — your journal, progress, and coaching history are all still here waiting for you.</p>
        </div>
      )}

      {canStartTrial && (
        <div className="mt-8 max-w-md mx-auto rounded-2xl border-2 border-[#C89B55] bg-[#FBF4E7] p-6 text-center" data-testid="paywall-trial-card">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Start free</p>
          <h2 className="font-display text-2xl font-semibold mt-1 text-[#291B2D]">Try Sisterhood+ free for 7 days</h2>
          <p className="text-sm text-[#59535B] mt-2">Full access to explore. Includes 5 complimentary AI sessions. No charge today.</p>
          <button data-testid="paywall-start-trial" onClick={startTrial} disabled={startingTrial}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] disabled:opacity-50 px-8 py-3.5 font-semibold transition-all">
            {startingTrial ? <><Loader2 className="h-4 w-4 animate-spin" /> Starting…</> : "Start my 7-day free trial"}
          </button>
          <p className="text-xs text-[#8A838E] mt-3">Or subscribe below for unlimited access.</p>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5 mt-10 max-w-md sm:max-w-none mx-auto">
        <button data-testid={PAYWALL.monthlyPlan} onClick={() => selectPlan("monthly")}
          className={`text-left rounded-2xl p-7 border-2 transition-all ${plan === "monthly" ? "border-[#C89B55] bg-[#F7F2EA]" : "border-[#E5DED4] bg-white"}`}>
          <p className="font-medium text-[#59535B]">Monthly</p>
          <p className="font-display text-4xl font-semibold mt-2">$14.99<span className="text-lg text-[#8A838E] font-normal">/mo</span></p>
          <p className="text-sm text-[#8A838E] mt-2">Flexible, cancel anytime</p>
        </button>

        <button data-testid={PAYWALL.annualPlan} onClick={() => selectPlan("annual")}
          className={`relative text-left rounded-2xl p-7 border-2 transition-all ${plan === "annual" ? "border-[#C89B55] bg-[#291B2D] text-[#FCFAF6]" : "border-[#E5DED4] bg-white"}`}>
          <span className="absolute -top-3 right-6 bg-[#C89B55] text-[#291B2D] text-xs font-bold px-3 py-1 rounded-full">Best value</span>
          <p className={`font-medium ${plan === "annual" ? "text-[#D6CFD9]" : "text-[#59535B]"}`}>Annual</p>
          <p className="font-display text-4xl font-semibold mt-2">$99.99<span className={`text-lg font-normal ${plan === "annual" ? "text-[#A298A6]" : "text-[#8A838E]"}`}>/yr</span></p>
          <p className={`text-sm mt-2 ${plan === "annual" ? "text-[#C89B55]" : "text-[#8A838E]"}`}>Save 44% · best value</p>
        </button>
      </div>

      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="mt-8 bg-[#F7F2EA] border border-[#E5DED4] rounded-2xl p-7 lg:p-8">
        <h2 className="font-display text-xl font-semibold flex items-center gap-2 mb-5">
          <Sparkles className="h-5 w-5 text-[#C89B55]" /> What your membership gives you
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {BENEFITS.map((b) => (
            <div key={b} className="flex items-start gap-3 text-[#252326]">
              <Check className="h-5 w-5 text-[#3D6B52] shrink-0 mt-0.5" /> {b}
            </div>
          ))}
        </div>
      </motion.div>

      <div className="mt-8 max-w-md mx-auto" data-testid={PAYWALL.paypalContainer}>
        {entitled ? (
          <div className="rounded-2xl border border-[#C89B55] bg-[#FBF4E7] p-6 text-center" data-testid="paywall-already-member">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">You're already a member</p>
            <h2 className="font-display text-2xl font-semibold mt-1 text-[#291B2D]">Your membership is active.</h2>
            <p className="text-sm text-[#59535B] mt-2">You don't need to subscribe again. Manage or change your plan anytime from Settings.</p>
            <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
              <button data-testid="paywall-go-home" onClick={() => navigate("/home")}
                className="inline-flex items-center justify-center rounded-full bg-[#291B2D] text-[#FCFAF6] hover:bg-[#352238] px-6 py-3 font-semibold transition-all">
                Back to Sisterhood+
              </button>
              <button data-testid="paywall-manage" onClick={() => navigate("/settings")}
                className="inline-flex items-center justify-center rounded-full border border-[#291B2D]/15 bg-white hover:bg-[#F7F2EA] px-6 py-3 font-semibold transition-all">
                Manage membership
              </button>
            </div>
          </div>
        ) : loadingCfg ? (
          <div className="flex items-center justify-center py-6 text-[#8A838E]">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading secure checkout…
          </div>
        ) : !config ? (
          <p className="text-center text-sm text-[#9E3A3A]">Checkout is temporarily unavailable. Please try again shortly.</p>
        ) : (
          <>
            {discountActive && (
              <div data-testid="paywall-discount-applied" className="mb-4 rounded-2xl border border-[#C89B55] bg-[#FBF4E7] p-4 text-center">
                <p className="text-sm text-[#7A5A1E]">
                  Code <span className="font-bold">{discount.code}</span> applied — your {discount.cadence} membership is{" "}
                  <span className="font-bold">${discount.discounted_value}</span>{" "}
                  <span className="line-through text-[#A9925F]">${discount.base_value}</span>
                </p>
              </div>
            )}
            {processing && (
              <div className="flex items-center justify-center py-3 text-[#8A838E]">
                <Loader2 className="h-4 w-4 animate-spin mr-2" /> Confirming your membership…
              </div>
            )}
            <PayPalScriptProvider options={{ clientId: config.client_id, components: "buttons", intent: "subscription", vault: true, currency: "USD" }}>
              <PayPalButtons
                data-testid={PAYWALL.subscribeButton}
                forceReRender={[planId]}
                style={{ layout: "vertical", shape: "pill", color: "gold", label: "subscribe" }}
                disabled={processing}
                createSubscription={() =>
                  api.post("/billing/create-subscription", { plan_id: planId })
                    .then(({ data }) => data.subscription_id)
                    .catch((e) => {
                      toast.error(e?.response?.data?.detail || "Couldn't start checkout. Please try again.");
                      throw e;
                    })
                }
                onApprove={onApprove}
                onError={() => toast.error("PayPal ran into a problem. Please try again.")}
                onCancel={() => toast("Checkout cancelled — you can subscribe anytime.")}
              />
            </PayPalScriptProvider>
          </>
        )}
      </div>
      <p className="text-center text-xs text-[#8A838E] mt-4">
        Secure recurring billing via PayPal. Cancel anytime from Settings.{" "}
        {discountActive
          ? `$${discount.discounted_value} billed ${plan === "annual" ? "yearly" : "monthly"} with code ${discount.code}.`
          : (plan === "annual" ? "$99.99 billed yearly." : "$14.99 billed monthly.")}
      </p>

      <div className="mt-6 max-w-md mx-auto">
        <PromoRedeem onRedeemed={() => navigate("/home")} onDiscount={applyDiscount} />
      </div>
    </div>
    </div>
  );
}
